import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Subscribe from "@/pages/Subscribe";
import SubscribePage from "@/pages/SubscribePage";
import Payment from "@/pages/Payment";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import ViewInvoice from "@/pages/ViewInvoice";
import Orders from "@/pages/Orders";
import NewOrder from "@/pages/NewOrder";
import ViewOrder from "@/pages/ViewOrder";
import Customers from "@/pages/Customers";
import ViewCustomer from "@/pages/ViewCustomer";
import Leads from "@/pages/Leads";
import Payments from "@/pages/Payments";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import TeamManagement from "@/pages/TeamManagement";
import TeamMemberProgress from "@/pages/TeamMemberProgress";
import Vendors from "@/pages/Vendors";
import ViewVendor from "@/pages/ViewVendor";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import IncomeExpenses from "@/pages/IncomeExpenses";
import CompanyAccount from "@/pages/CompanyAccount";
import WorkflowTickets from "@/pages/WorkflowTickets";

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
        }
      />
      <Route path="/subscribe" element={<SubscribePage />} />
      <Route path="/subscribe/:packageId" element={<Subscribe />} />
      <Route
        path="/payment/:subscriptionId"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Payment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <Layout>
              <Invoices />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/new"
        element={
          <ProtectedRoute requireRole="sales,admin">
            <Layout>
              <NewOrder />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ViewInvoice />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute requireRole="sales,admin">
            <Layout>
              <Orders />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/new"
        element={
          <ProtectedRoute requireRole="sales,admin">
            <Layout>
              <NewOrder />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id/edit"
        element={
          <ProtectedRoute requireRole="sales,admin">
            <Layout>
              <NewOrder />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute requireRole="sales,admin">
            <Layout>
              <ViewOrder />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute requireRole="sales,admin">
            <Layout>
              <Leads />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <Layout>
              <Customers />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ViewCustomer />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <Layout>
              <Payments />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflow-tickets"
        element={
          <ProtectedRoute>
            <Layout>
              <WorkflowTickets />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute requireRole="admin,finance,management,sales">
            <Layout>
              <Reports />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/company-account"
        element={
          <ProtectedRoute requireRole="admin,finance,management">
            <Layout>
              <CompanyAccount />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requireRole="admin">
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/settings/invoice-template" element={<Navigate to="/settings" replace />} />
      <Route
        path="/vendors"
        element={
          <ProtectedRoute requireRole="admin,finance,management,sales">
            <Layout>
              <Vendors />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:id"
        element={
          <ProtectedRoute requireRole="admin,finance,management,sales">
            <Layout>
              <ViewVendor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute requireRole="admin">
            <Layout>
              <TeamManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/:id"
        element={
          <ProtectedRoute requireRole="admin">
            <Layout>
              <TeamMemberProgress />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/income-expenses"
        element={
          <ProtectedRoute requireRole="finance,admin,management">
            <Layout>
              <IncomeExpenses />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <SuperAdminDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
